import { generatedApi } from './api.generated';
import { createEndpointExports, createHookExports, provideListTags } from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { USERS_API_ENDPOINTS as cfg } from './config';

/**
 * Wrapper around the generated Users RTK Query API.
 *
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `Users` tag type is used for both the collection and individual
 * entities. The list query provides a per-item tag for every user plus a
 * dedicated `{ type: 'Users', id: 'LIST' }` tag, so that:
 * - mutating a single user (`updateUser`, `blockUser`, …) invalidates only that
 *   user's tag, which refetches both the affected `getUser` query and the list
 *   (the list provides that same per-item tag);
 * - creating/removing a user would invalidate `{ id: 'LIST' }` to refetch the
 *   collection without touching unrelated entities.
 *
 * This removes the previous manual `dispatch(invalidateTags(...))` workaround in
 * `onQueryStarted`: that was attributed to redux-toolkit#5093, but that issue is
 * about the `isSuccess` flag during pessimistic updates, not tag invalidation —
 * `invalidatesTags` propagates to the list correctly via the per-item tags.
 */
const enhancedUsersApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Users'],
  endpoints: {
    [cfg.listUsers.generated]: {
      providesTags: (result) => provideListTags(result, 'Users'),
    },
    [cfg.getUser.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.id }],
    },
    [cfg.updateUser.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.id }],
    },
    [cfg.blockUser.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.id }],
    },
    [cfg.unblockUser.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.id }],
    },
    [cfg.setUserRoles.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.id }],
    },
    [cfg.resetUserMfa.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.id }],
    },
  },
});

export const usersApi: typeof enhancedUsersApi = enhancedUsersApi;

export const hookExports: HookExports<typeof cfg, typeof usersApi> = createHookExports(
  cfg,
  usersApi,
);

const endpointExports = createEndpointExports(cfg, usersApi);

export const usersApiEndpoints: typeof endpointExports = endpointExports;
